import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { RegisterButton } from '../../../../components/Button/RegisterButton';
import { PageHeader } from '../../../../components/PageHeader';
import { Table } from '../../../../components/Table/Table';
import WhiteContainer from '../../../../components/WhiteContainer/WhiteContainer';
import useUserState from '../../../../services/angular/angularUserState';
import { api } from '../../../../services/angular/axios';
import { SortOptions } from '../../../../services/angular/types/Params';
import AddNewDriverForm from './components/addNewDriverForm';
import { Driver } from '../../../../services/angular/types/Order';
import { useAlert } from '../../../../contexts/AlertContext';
import { dateFormat } from '../../../../helpers/format/dateFormat';
import { phoneMask, removePhoneMask } from '../../../../helpers/mask/phoneMask';
import { useCopy } from '../../../../hooks/useCopy';

const AngularDrivers: React.FC = () => {
	const alert = useAlert();
	const copy = useCopy();
	const company = useUserState((state) => state.company);
	const [addNewDriverForm, setAddNewDriverForm] = useState(false);
	const [params, setParams] = useState<{
		page: number;
		take: number;
		sortBy?: SortOptions;
	}>({
		page: 1,
		take: 10,
	});

	const { data, isLoading, isFetching, refetch } = useQuery(
		['companyUsers', params, company?.id],
		async () =>
			await api.get(
				// eslint-disable-next-line @typescript-eslint/restrict-template-expressions
				`/companies/${company?.id}/drivers`,
				{
					params: {
						page: params.page,
						pageSize: params.take,
						sort: params.sortBy
							? `${params.sortBy?.id}:${params.sortBy?.order}`
							: undefined,
					},
				}
			),
		{
			refetchOnMount: 'always',
		}
	);

	const deleteDriver = async (id: number) => {
		alert.onCustom({
			title: 'Excluir motorista',
			message: 'Deseja realmente excluir o motorista?',
			confirm: {
				label: 'Excluir',
				onClick: async () => {
					try {
						await api.delete(`/companies/${company?.id || ''}/drivers/${id}`);
						toast.success('Motorista excluído com sucesso');
						void refetch();
					} catch (error) {
						toast.error('Erro ao excluir motorista');
					}
				},
			},
		});
	};

	return (
		<>
			{addNewDriverForm ? (
				<AddNewDriverForm />
			) : (
				<>
					<PageHeader
						title="Motoristas"
						description="Relação de motoristas cadastrados"
					>
						<RegisterButton
							className="w-auto"
							onClick={() => setAddNewDriverForm(true)}
						/>
					</PageHeader>
					<WhiteContainer className="mt-8">
						<Table<Driver>
							isLoading={isLoading || isFetching}
							data={data?.data.data || []}
							total={data?.data.meta.pagination.total || 0}
							sort
							onParamsChange={(params) => {
								setParams(params);
							}}
							actions={[
								{
									icon: 'trash',
									label: 'Excluir',
									onAction: (data) => {
										void deleteDriver(data.id);
									},
								},
							]}
							columns={[
								{
									accessor: 'name',
									Header: 'Nome',
								},
								{
									accessor: 'cpf',
									Header: 'CPF',
								},
								{
									accessor: 'driverLicenseExpiration',
									Header: 'Venc. CNH',
									Cell: ({ value }) => (
										<div className="flex">{dateFormat(value)}</div>
									),
								},
								{
									accessor: 'cellphone',
									Header: 'Celular',
									Cell: ({ value }) => (
										<div
											className=" flex cursor-copy"
											onClick={() => {
												void copy(removePhoneMask(value, '+55'));
											}}
										>
											{phoneMask(removePhoneMask(value))}
										</div>
									),
								},
								{
									accessor: 'email',
									Header: 'Email',
								},
								{
									accessor: 'driverStatus',
									Header: 'Status',
									Cell: ({ value }) => (
										<span className="flex">
											{value === 1 ? (
												<div className="w-fit rounded bg-orange px-1.5 py-px text-center text-xs text-white">
													Aguardando aprovação
												</div>
											) : value === 2 ? (
												<div className="w-fit rounded bg-terracota-500 px-1.5 py-px text-center text-xs text-white">
													Desaprovado
												</div>
											) : value === 3 ? (
												<div className="w-fit rounded bg-green px-1.5 py-px text-center text-xs text-white">
													Aprovado
												</div>
											) : (
												<div className="bg-gray w-fit rounded px-1.5 py-px text-center text-xs text-white"></div>
											)}
										</span>
									),
								},
							]}
						/>
					</WhiteContainer>
				</>
			)}
		</>
	);
};

export default AngularDrivers;
